import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import confetti from 'canvas-confetti';
import { getDiagnosticById, SavedDiagnostic } from '../services/diagnosticService';
import { verifyAndUnlockPayment } from '../services/paymentService';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { dpsTheme } from '../styles/dpsTheme';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(5);
  const [diagnostic, setDiagnostic] = useState<SavedDiagnostic | null>(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const diagnosticId = searchParams.get('diagnostic_id');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const pollDiagnosticStatus = async () => {
      if (!diagnosticId || !sessionId) {
        setError('Paramètres de paiement manquants');
        toast.error('Paramètres de paiement manquants');
        setVerifying(false);
        setLoading(false);
        return;
      }

      try {
        setVerifying(true);

        let attempts = 0;
        const maxAttempts = 10;
        const pollInterval = 200;

        const checkStatus = async (): Promise<boolean> => {
          const { data: diagnostic, error } = await supabase
            .from('diagnostics')
            .select('report_paid')
            .eq('id', diagnosticId)
            .maybeSingle();

          if (error) {
            console.error('Error checking diagnostic status:', error);
            return false;
          }

          return diagnostic?.report_paid === true;
        };

        const poll = async () => {
          while (attempts < maxAttempts) {
            const isUnlocked = await checkStatus();

            if (isUnlocked) {
              setVerifying(false);

              const data = await getDiagnosticById(diagnosticId);
              if (data) {
                setDiagnostic(data);
                confetti({
                  particleCount: 100,
                  spread: 70,
                  origin: { y: 0.6 }
                });
              } else {
                setError('Diagnostic non trouvé');
                toast.error('Diagnostic non trouvé');
              }
              setLoading(false);
              return;
            }

            attempts++;
            if (attempts < maxAttempts) {
              await new Promise(resolve => setTimeout(resolve, pollInterval));
            }
          }

          const verificationResult = await verifyAndUnlockPayment(sessionId);

          if (!verificationResult.success) {
            setError(verificationResult.error || 'Erreur lors de la vérification du paiement');
            toast.error(verificationResult.error || 'Erreur lors de la vérification du paiement');
            setVerifying(false);
            setLoading(false);
            return;
          }

          setVerifying(false);

          const data = await getDiagnosticById(diagnosticId);
          if (data) {
            setDiagnostic(data);
            confetti({
              particleCount: 100,
              spread: 70,
              origin: { y: 0.6 }
            });
          } else {
            setError('Diagnostic non trouvé');
            toast.error('Diagnostic non trouvé');
          }
          setLoading(false);
        };

        poll();
      } catch (error) {
        console.error('Error checking payment:', error);
        setError('Erreur lors de la vérification du paiement');
        toast.error('Erreur lors de la vérification du paiement');
        setVerifying(false);
        setLoading(false);
      }
    };

    pollDiagnosticStatus();
  }, [diagnosticId, sessionId]);

  useEffect(() => {
    if (!loading && diagnostic) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate(`/dps/report/${diagnosticId}`);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [loading, diagnostic, diagnosticId, navigate]);

  if (loading || verifying) {
    return (
      <div style={{
        minHeight: '100vh',
        background: dpsTheme.colors.background.primary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: dpsTheme.spacing.xl
      }}>
        <div style={{ maxWidth: '500px', width: '100%' }}>
          <div style={{
            background: dpsTheme.colors.background.card,
            backdropFilter: 'blur(20px)',
            border: `1px solid ${dpsTheme.colors.border.primary}`,
            borderRadius: dpsTheme.borderRadius.lg,
            padding: dpsTheme.spacing['3xl'],
            textAlign: 'center',
            boxShadow: dpsTheme.shadows.card
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              border: `3px solid ${dpsTheme.colors.border.primary}`,
              borderTop: `3px solid ${dpsTheme.colors.accent.blue.primary}`,
              borderRadius: '50%',
              margin: '0 auto',
              marginBottom: dpsTheme.spacing.xl,
              animation: 'spin 1s linear infinite'
            }}></div>
            <h2 style={{
              fontSize: dpsTheme.typography.fontSize['2xl'],
              fontWeight: dpsTheme.typography.fontWeight.semibold,
              color: dpsTheme.colors.text.primary,
              marginBottom: dpsTheme.spacing.md
            }}>
              {verifying ? 'Finalisation de votre paiement...' : 'Chargement...'}
            </h2>
            <p style={{
              fontSize: dpsTheme.typography.fontSize.base,
              color: dpsTheme.colors.text.muted
            }}>
              {verifying ? 'Traitement en cours par notre système sécurisé' : 'Veuillez patienter'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !diagnostic) {
    return (
      <div style={{
        minHeight: '100vh',
        background: dpsTheme.colors.background.primary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: dpsTheme.spacing.xl
      }}>
        <div style={{ maxWidth: '500px', width: '100%' }}>
          <div style={{
            background: dpsTheme.colors.background.card,
            backdropFilter: 'blur(20px)',
            border: `1px solid ${dpsTheme.colors.border.primary}`,
            borderRadius: dpsTheme.borderRadius.lg,
            padding: dpsTheme.spacing['3xl'],
            textAlign: 'center',
            boxShadow: dpsTheme.shadows.card
          }}>
            <div style={{ marginBottom: dpsTheme.spacing.xl }}>
              <ExclamationTriangleIcon style={{
                height: '80px',
                width: '80px',
                color: dpsTheme.colors.priority.high.text,
                margin: '0 auto'
              }} />
            </div>
            <h1 style={{
              fontSize: dpsTheme.typography.fontSize['3xl'],
              fontWeight: dpsTheme.typography.fontWeight.bold,
              color: dpsTheme.colors.text.primary,
              marginBottom: dpsTheme.spacing.lg
            }}>
              Erreur de vérification
            </h1>
            <p style={{
              fontSize: dpsTheme.typography.fontSize.base,
              color: dpsTheme.colors.text.muted,
              marginBottom: dpsTheme.spacing.xl
            }}>
              {error || 'Impossible de charger le diagnostic.'}
            </p>
            <div style={{
              background: 'rgba(251, 191, 36, 0.1)',
              border: `1px solid rgba(251, 191, 36, 0.3)`,
              borderRadius: dpsTheme.borderRadius.md,
              padding: dpsTheme.spacing.lg,
              marginBottom: dpsTheme.spacing.xl
            }}>
              <p style={{
                fontSize: dpsTheme.typography.fontSize.sm,
                color: '#92400e'
              }}>
                <strong>Note:</strong> Votre paiement a peut-être été traité.
                Veuillez vérifier votre tableau de bord ou contacter le support.
              </p>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                width: '100%',
                background: dpsTheme.colors.accent.blue.gradient,
                color: '#ffffff',
                padding: `${dpsTheme.spacing.md} ${dpsTheme.spacing.xl}`,
                borderRadius: dpsTheme.borderRadius.md,
                fontSize: dpsTheme.typography.fontSize.base,
                fontWeight: dpsTheme.typography.fontWeight.semibold,
                border: 'none',
                cursor: 'pointer',
                marginBottom: dpsTheme.spacing.md,
                boxShadow: dpsTheme.shadows.button,
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = dpsTheme.shadows.hover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = dpsTheme.shadows.button;
              }}
            >
              Retour au tableau de bord
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{
                width: '100%',
                background: dpsTheme.colors.background.darker,
                color: dpsTheme.colors.text.secondary,
                padding: `${dpsTheme.spacing.md} ${dpsTheme.spacing.xl}`,
                borderRadius: dpsTheme.borderRadius.md,
                fontSize: dpsTheme.typography.fontSize.base,
                fontWeight: dpsTheme.typography.fontWeight.medium,
                border: `1px solid ${dpsTheme.colors.border.primary}`,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = dpsTheme.colors.background.card;
                e.currentTarget.style.borderColor = dpsTheme.colors.border.hover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = dpsTheme.colors.background.darker;
                e.currentTarget.style.borderColor = dpsTheme.colors.border.primary;
              }}
            >
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: dpsTheme.colors.background.primary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: dpsTheme.spacing.xl
    }}>
      <div style={{ maxWidth: '500px', width: '100%' }}>
        <div style={{
          background: dpsTheme.colors.background.card,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${dpsTheme.colors.border.primary}`,
          borderRadius: dpsTheme.borderRadius.lg,
          padding: dpsTheme.spacing['3xl'],
          textAlign: 'center',
          boxShadow: dpsTheme.shadows.card
        }}>
          <div style={{ marginBottom: dpsTheme.spacing.xl }}>
            <CheckCircleIcon style={{
              height: '80px',
              width: '80px',
              color: dpsTheme.colors.accent.green.primary,
              margin: '0 auto'
            }} />
          </div>

          <h1 style={{
            fontSize: dpsTheme.typography.fontSize['4xl'],
            fontWeight: dpsTheme.typography.fontWeight.bold,
            color: dpsTheme.colors.text.primary,
            marginBottom: dpsTheme.spacing.md
          }}>
            Paiement réussi !
          </h1>

          <p style={{
            fontSize: dpsTheme.typography.fontSize.base,
            color: dpsTheme.colors.text.muted,
            marginBottom: dpsTheme.spacing.lg
          }}>
            Votre rapport DPS Premium est maintenant disponible.
          </p>

          <div style={{
            background: dpsTheme.colors.accent.blue.bg,
            border: `1px solid ${dpsTheme.colors.accent.blue.border}`,
            borderRadius: dpsTheme.borderRadius.md,
            padding: dpsTheme.spacing.md,
            marginBottom: dpsTheme.spacing.xl
          }}>
            <p style={{
              fontSize: dpsTheme.typography.fontSize.base,
              fontWeight: dpsTheme.typography.fontWeight.semibold,
              color: dpsTheme.colors.accent.blue.primary
            }}>
              {diagnostic.diagnostic_data?.city || 'Ville'}
            </p>
            <p style={{
              fontSize: dpsTheme.typography.fontSize.sm,
              color: dpsTheme.colors.accent.blue.secondary,
              marginTop: '4px'
            }}>
              Score de sécurité : {diagnostic.security_score || 0}/100
            </p>
          </div>

          <div style={{
            background: dpsTheme.colors.accent.green.bg,
            border: `1px solid ${dpsTheme.colors.accent.green.border}`,
            borderRadius: dpsTheme.borderRadius.md,
            padding: dpsTheme.spacing.lg,
            marginBottom: dpsTheme.spacing.xl,
            textAlign: 'left'
          }}>
            <div style={{
              fontSize: dpsTheme.typography.fontSize.sm,
              color: dpsTheme.colors.accent.green.primary,
              marginBottom: dpsTheme.spacing.sm
            }}>
              ✓ Paiement validé
            </div>
            <div style={{
              fontSize: dpsTheme.typography.fontSize.sm,
              color: dpsTheme.colors.accent.green.primary,
              marginBottom: dpsTheme.spacing.sm
            }}>
              ✓ Rapport DPS débloqué
            </div>
            <div style={{
              fontSize: dpsTheme.typography.fontSize.sm,
              color: dpsTheme.colors.accent.green.primary
            }}>
              ✓ Accès à vie activé
            </div>
          </div>

          <p style={{
            fontSize: dpsTheme.typography.fontSize.sm,
            color: dpsTheme.colors.text.muted,
            marginBottom: dpsTheme.spacing.xl
          }}>
            Redirection automatique vers votre rapport dans{' '}
            <span style={{
              fontWeight: dpsTheme.typography.fontWeight.bold,
              color: dpsTheme.colors.accent.blue.primary
            }}>
              {countdown}
            </span>
            {' '}seconde{countdown > 1 ? 's' : ''}...
          </p>

          <button
            onClick={() => navigate(`/dps/report/${diagnosticId}`)}
            style={{
              width: '100%',
              background: dpsTheme.colors.accent.blue.gradient,
              color: '#ffffff',
              padding: `${dpsTheme.spacing.md} ${dpsTheme.spacing.xl}`,
              borderRadius: dpsTheme.borderRadius.md,
              fontSize: dpsTheme.typography.fontSize.base,
              fontWeight: dpsTheme.typography.fontWeight.bold,
              border: 'none',
              cursor: 'pointer',
              marginBottom: dpsTheme.spacing.md,
              boxShadow: dpsTheme.shadows.button,
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = dpsTheme.shadows.hover;
              e.currentTarget.style.transform = 'scale(1.02)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = dpsTheme.shadows.button;
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            Voir mon rapport premium maintenant
          </button>

          <button
            onClick={() => navigate('/dashboard')}
            style={{
              width: '100%',
              background: dpsTheme.colors.background.darker,
              color: dpsTheme.colors.text.secondary,
              padding: `${dpsTheme.spacing.md} ${dpsTheme.spacing.xl}`,
              borderRadius: dpsTheme.borderRadius.md,
              fontSize: dpsTheme.typography.fontSize.base,
              fontWeight: dpsTheme.typography.fontWeight.medium,
              border: `1px solid ${dpsTheme.colors.border.primary}`,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = dpsTheme.colors.background.card;
              e.currentTarget.style.borderColor = dpsTheme.colors.border.hover;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = dpsTheme.colors.background.darker;
              e.currentTarget.style.borderColor = dpsTheme.colors.border.primary;
            }}
          >
            Retour au tableau de bord
          </button>

          <p style={{
            marginTop: dpsTheme.spacing.lg,
            fontSize: dpsTheme.typography.fontSize.xs,
            color: dpsTheme.colors.text.muted
          }}>
            Un email de confirmation vous a été envoyé.
          </p>
        </div>
      </div>
    </div>
  );
}
